// صفحهٔ «بروزرسانی نرم‌افزار» — فقط کد و نسخه، هرگز اطلاعاتِ کاربران.
// جریانِ دومرحله‌ای: اول بسته دریافت و بررسی می‌شود (staging)، بعد نصب.
// به همین دلیل قطعیِ اینترنت وسطِ دانلود نمی‌تواند نسخهٔ فعلی را خراب کند.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Descriptions, Input, Modal, Result, Row, Space, Statistic, Steps, Table, Tag, Timeline, Typography, Upload, message } from "antd";
import Alert from "../../components/GxAlert";
import {
  CheckCircleOutlined, CloudDownloadOutlined, HeartOutlined, ReloadOutlined,
  RollbackOutlined, UploadOutlined,
} from "@ant-design/icons";
import {
  updateApplyStaged, updateApplyScheduled, updateHealth, updateHistory,
  updateRollback, updateStage,
  updateStageDiscard, updateStageLink, updateVersion,
} from "../../api/client";
import DismissibleHelp from "../../components/DismissibleHelp";
import DangerConfirmModal from "./backup/DangerConfirmModal";
import MaintenanceAnnounceCard from "./MaintenanceAnnounceCard";

const { Text, Paragraph } = Typography;

function StepTimeline({ log }) {
  if (!log?.length) return null;
  return (
    <Timeline items={log.map((s) => ({
      color: s.status === "ok" ? "green" : s.status === "info" ? "blue" : "red",
      children: (
        <div>
          <Text strong>{s.step}</Text>
          {s.at && <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>{s.at.slice(11, 19)}</Text>}
          {s.detail && (
            <div style={{
              fontSize: 12, color: "#888", whiteSpace: "pre-wrap",
              maxHeight: 160, overflow: "auto",
            }}>{s.detail}</div>
          )}
        </div>
      ),
    }))} />
  );
}

export default function UpdateTab() {
  const [ver, setVer] = useState(null);
  const [staged, setStaged] = useState(null);       // نتیجهٔ اعتبارسنجیِ بستهٔ آماده
  const [busy, setBusy] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [health, setHealth] = useState(null);

  const loadVer = useCallback(() => {
    updateVersion().then((r) => setVer(r.data)).catch(() => {});
  }, []);
  const loadHistory = useCallback(() => {
    updateHistory(20).then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadVer(); loadHistory(); }, [loadVer, loadHistory]);

  const step = result ? 4 : staged ? 2 : 0;

  // ── مرحلهٔ ۱: دریافتِ بسته (بدونِ هیچ نصبی) ──
  const onPick = async (file) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await updateStage(file);
      if (!data.ok) { message.error(data.message || "بسته معتبر نیست"); setStaged(null); }
      else { setStaged(data); message.success("بسته دریافت و بررسی شد"); }
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در دریافتِ بسته"); }
    setBusy(false);
    return false;   // جلوگیری از آپلودِ خودکارِ AntD
  };

  const fromLink = async () => {
    if (!linkUrl.trim().startsWith("https://")) return message.warning("فقط لینکِ https پذیرفته می‌شود");
    setBusy(true); setResult(null);
    try {
      const { data } = await updateStageLink(linkUrl.trim());
      setStaged(data);
      message.success(`بسته در ${data.attempts} تلاش دریافت و بررسی شد`);
    } catch (e) { message.error(e?.response?.data?.detail || "دانلود ناموفق بود"); }
    setBusy(false);
  };

  const discard = async () => {
    if (staged?.staging_id) { try { await updateStageDiscard(staged.staging_id); } catch { /* بی‌اهمیت */ } }
    setStaged(null); setResult(null);
  };

  // ── مرحلهٔ ۲: نصب ──
  /* دو راه، یک بسته: «با اجازه» پیام غیرمزاحمی به کاربرانِ آنلاین می‌دهد و
     اگر کسی جواب ندهد سرِ مهلت خودش نصب می‌شود؛ «فوری» همان لحظه می‌زند.
     پیش‌فرض «با اجازه» است، چون قطعِ بی‌خبرِ دسترسی همان چیزی بود که شکایت شد. */
  const [askUsers, setAskUsers] = useState(true);
  const install = async ({ admin_pin, confirm_text }) => {
    setInstalling(true);
    try {
      if (askUsers) {
        const { data } = await updateApplyScheduled({
          staging_id: staged.staging_id, admin_pin, confirm_text,
          ask_seconds: 120, max_delays: 2, delay_seconds: 300,
        });
        setConfirmOpen(false);
        setStaged(null);
        message.success(data.message || "از کاربران اجازه گرفته شد", 8);
      } else {
        const { data } = await updateApplyStaged({
          staging_id: staged.staging_id, admin_pin, confirm_text,
        });
        setResult(data);
        setConfirmOpen(false);
        setStaged(null);
        if (data.ok) message.success("بروزرسانی انجام شد");
        else message.warning(data.message);
      }
      loadVer(); loadHistory();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در نصب"); }
    setInstalling(false);
  };

  const doRollback = async ({ admin_pin, confirm_text }) => {
    setBusy(true);
    try {
      const { data } = await updateRollback({ admin_pin, confirm_text });
      Modal[data.ok ? "success" : "error"]({
        title: data.ok ? "بازگشت انجام شد" : "بازگشت ناموفق بود",
        content: data.detail,
      });
      setRollbackOpen(false);
      loadVer(); loadHistory();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
    setBusy(false);
  };

  const checkHealth = async () => {
    try { const { data } = await updateHealth(); setHealth(data); }
    catch { message.error("خطا در بررسیِ سلامت"); }
  };

  const histCols = [
    { title: "زمان", dataIndex: "at", width: 150, render: (v) => (v || "").replace("T", " ").slice(0, 16) },
    { title: "از نسخه", dataIndex: "from", width: 140 },
    { title: "به نسخه", dataIndex: "to", width: 140 },
    { title: "فایل", dataIndex: "written", width: 70 },
    { title: "توسط", dataIndex: "actor", width: 100 },
    { title: "منبع", dataIndex: "source", width: 100 },
    {
      title: "نتیجه", dataIndex: "ok", width: 110,
      render: (v, r) => (
        <Tag color={v ? "green" : r.rolled_back ? "orange" : "red"}>
          {v ? "موفق" : r.rolled_back ? "برگشت" : "ناموفق"}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ direction: "rtl" }}>
      <DismissibleHelp id="update-tab"
        message="بروزرسانی فقط کد و نسخهٔ نرم‌افزار را عوض می‌کند — دیتابیس و فایل‌های شما پاک یا جایگزین نمی‌شوند."
        description="بسته اول کاملاً دریافت و بررسی می‌شود و تنها در صورتِ سالم‌بودن نصب می‌گردد. پیش از نصب، بکاپِ خودکارِ دیتابیس و فایل‌ها گرفته می‌شود و اگر هر مرحله شکست بخورد، نسخهٔ قبلی خودکار برمی‌گردد." />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card size="small" title="نسخهٔ فعلی"
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadVer} />}>
            <Statistic value={ver?.semver || "—"} prefix="v" valueStyle={{ fontSize: 24 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>مهرِ بسته: {ver?.version || "—"}</Text>
            {ver?.update_available && (
              <Alert style={{ marginTop: 10 }} type="info" showIcon
                message={`نسخهٔ موجود: ${ver.latest_known}`} />
            )}
            {/* بدونِ پینِ مدیریتی نصب ممکن نیست — **قبل** از آپلود گفته شود،
                نه بعد از آپلودِ کامل با یک ۴۰۳ گنگ. */}
            {ver && ver.admin_pin_configured === false && (
              <Alert style={{ marginTop: 10 }} type="error" showIcon
                message="پینِ مدیریتی تنظیم نشده — نصبِ آپدیت ممکن نیست"
                description="از «تنظیمات ← کاربران» برای مدیرِ اصلی یک پین بگذارید، وگرنه در مرحلهٔ آخر خطای دسترسی می‌گیرید." />
            )}
            {ver?.release_notes && (
              <Card size="small" title="توضیحاتِ نسخه" style={{ marginTop: 10 }}>
                <Paragraph style={{
                  fontSize: 12, maxHeight: 160, overflow: "auto",
                  whiteSpace: "pre-wrap", marginBottom: 0,
                }}>{ver.release_notes}</Paragraph>
              </Card>
            )}
            <Space style={{ marginTop: 10 }} wrap>
              <Button size="small" icon={<HeartOutlined />} onClick={checkHealth}>بررسی سلامت</Button>
              <Button size="small" danger icon={<RollbackOutlined />}
                onClick={() => setRollbackOpen(true)}>بازگشت به نسخهٔ قبل</Button>
            </Space>
            {health && (
              <div style={{ marginTop: 10 }}>
                <Alert type={health.ok ? "success" : "error"} showIcon message={health.summary} />
                {health.checks.map((c) => (
                  <div key={c.name} style={{ fontSize: 12, marginTop: 4 }}>
                    <Tag color={c.ok ? "green" : "red"}>{c.ok ? "✓" : "✗"}</Tag>
                    {c.name} — <Text type="secondary">{c.detail}</Text>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <MaintenanceAnnounceCard />
        </Col>

        <Col xs={24} lg={16}>
          <Card size="small" title="نصبِ بروزرسانی">
            <Steps size="small" current={step} style={{ marginBottom: 16 }}
              items={[
                { title: "دریافت بسته" },
                { title: "بررسی صحت" },
                { title: "تأیید نهایی" },
                { title: "نصب" },
                { title: "نتیجه" },
              ]} />

            {!staged && !result && (
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Card size="small" title="آپلودِ فایلِ بروزرسانی">
                    <Upload beforeUpload={onPick} showUploadList={false} accept=".zip" disabled={busy}>
                      <Button type="primary" icon={<UploadOutlined />} loading={busy} block>
                        انتخابِ فایل ZIP
                      </Button>
                    </Upload>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title="دریافت از لینک (فضای ابری)">
                    <Space.Compact style={{ width: "100%" }}>
                      <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://…/gharchak-update.zip" />
                      <Button icon={<CloudDownloadOutlined />} loading={busy} onClick={fromLink}>
                        دریافت
                      </Button>
                    </Space.Compact>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                      دانلود با تلاشِ مجدد و ادامه از محلِ قطع انجام می‌شود؛ فایلِ ناقص هرگز نصب نمی‌شود.
                    </Text>
                  </Card>
                </Col>
              </Row>
            )}

            {staged && (
              <>
                <Alert type="success" showIcon style={{ marginBottom: 12 }}
                  message="بسته سالم است و آمادهٔ نصب" description={staged.message} />
                <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
                  <Descriptions.Item label="نسخهٔ بسته">{staged.version}</Descriptions.Item>
                  <Descriptions.Item label="نسخهٔ فعلی">{staged.prev_version}</Descriptions.Item>
                  <Descriptions.Item label="تعداد فایل">{staged.file_count}</Descriptions.Item>
                  <Descriptions.Item label="زمانِ ساخت">
                    {(staged.built_at || "").replace("T", " ").slice(0, 16)}
                  </Descriptions.Item>
                  <Descriptions.Item label="چک‌سام" span={2}>
                    <Text code style={{ fontSize: 11 }}>{(staged.sha256 || "").slice(0, 32)}…</Text>
                  </Descriptions.Item>
                </Descriptions>
                {staged.migrations?.length > 0 && (
                  <Alert style={{ marginTop: 12 }} type="warning" showIcon
                    message={`این بسته ${staged.migrations.length} مهاجرتِ دیتابیس دارد`}
                    description={staged.migrations.join("، ")} />
                )}
                {staged.release_notes && (
                  <Card size="small" title="توضیحاتِ این نسخه" style={{ marginTop: 12 }}>
                    <Paragraph style={{
                      fontSize: 12, maxHeight: 200, overflow: "auto",
                      whiteSpace: "pre-wrap", marginBottom: 0,
                    }}>{staged.release_notes}</Paragraph>
                  </Card>
                )}
                <Space style={{ marginTop: 12 }}>
                  <Button type="primary" icon={<CheckCircleOutlined />}
                    onClick={() => { setAskUsers(true); setConfirmOpen(true); }}>
                    نصب با اجازهٔ کاربران
                  </Button>
                  <Button danger icon={<CheckCircleOutlined />}
                    onClick={() => { setAskUsers(false); setConfirmOpen(true); }}>
                    نصبِ فوری (بدونِ پرسیدن)
                  </Button>
                  <Button onClick={discard}>انصراف و حذفِ بسته</Button>
                </Space>
              </>
            )}

            {result && (
              <>
                <Result status={result.ok ? "success" : result.rolled_back ? "warning" : "error"}
                  title={result.message}
                  subTitle={`${result.prev_version} → ${result.version || "؟"} — ${result.written} فایل`}
                  extra={<Button onClick={() => setResult(null)}>بستن</Button>} />
                <Card size="small" title="لاگِ کاملِ بروزرسانی">
                  <StepTimeline log={result.log} />
                </Card>
                {result.errors?.length > 0 && (
                  <Alert style={{ marginTop: 12 }} type="error" showIcon
                    message="خطاها" description={result.errors.join(" | ")} />
                )}
              </>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card size="small" title="تاریخچهٔ بروزرسانی"
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={loadHistory}>تازه‌سازی</Button>}>
            <Table rowKey={(r, i) => `${r.at}-${i}`} size="small" columns={histCols}
              dataSource={history} pagination={{ pageSize: 8, size: "small" }}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: "هنوز بروزرسانی‌ای ثبت نشده است" }} />
          </Card>
        </Col>
      </Row>

      <DangerConfirmModal
        open={confirmOpen} confirmLoading={installing} word="بروزرسانی"
        title="تأییدِ نصبِ بروزرسانی" okText="نصب کن"
        warning={`نسخهٔ ${staged?.prev_version} به ${staged?.version} تغییر می‌کند.`}
        checklist={[
          askUsers
            ? "به کاربرانِ آنلاین پیام می‌رود؛ می‌توانند «همین حالا» یا «۵ دقیقه دیگر» را بزنند و اگر کسی کاری نکند تا ۲ دقیقهٔ دیگر خودش نصب می‌شود"
            : "🔴 بدونِ پرسیدن، همین حالا نصب می‌شود و دسترسیِ کاربران قطع می‌گردد",
          "بکاپِ خودکارِ دیتابیس گرفته می‌شود",
          "بکاپِ خودکارِ فایل‌ها گرفته می‌شود",
          "عکسِ برگشت از فایل‌های بازنویسی‌شونده گرفته می‌شود",
          staged?.migrations?.length
            ? `${staged.migrations.length} مهاجرتِ دیتابیس اجرا می‌شود`
            : "مهاجرتِ جدیدی ندارد",
          "بعد از نصب، سلامتِ سیستم و حفظِ داده بررسی می‌شود",
          "در صورتِ شکست، نسخهٔ قبلی خودکار برمی‌گردد",
        ]}
        onCancel={() => setConfirmOpen(false)} onConfirm={install} />

      <DangerConfirmModal
        open={rollbackOpen} confirmLoading={busy} word="بازگشت"
        title="بازگشت به نسخهٔ قبل" okText="برگردان"
        warning="فایل‌های آخرین بروزرسانی به نسخهٔ قبلشان برگردانده می‌شوند."
        checklist={[
          "آخرین عکسِ برگشت استفاده می‌شود",
          "دیتابیس دست‌نخورده می‌ماند (در صورتِ نیاز از تبِ «بازیابی» اقدام کنید)",
        ]}
        onCancel={() => setRollbackOpen(false)} onConfirm={doRollback} />
    </div>
  );
}
